import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { BiArrowBack } from 'react-icons/bi';
import { platformAPI } from '../../../services/platformAPI';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import './CustomerMaster.scss';
import './TimesheetsPage.scss';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const formatDay = (iso, withYear = false) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-IN', {
  day: 'numeric',
  month: 'short',
  year: withYear ? 'numeric' : undefined,
  timeZone: 'UTC'
});

const shiftWeek = (iso, days) => {
  const date = new Date(`${iso}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

const weekStart = (iso, startsOn) => {
  const date = new Date(`${iso}T00:00:00Z`);
  const delta = (date.getUTCDay() - Number(startsOn || 1) + 7) % 7;
  date.setUTCDate(date.getUTCDate() - delta);
  return date.toISOString().slice(0, 10);
};

const TimesheetWeekPage = () => {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [anchor, setAnchor] = useState(() => new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState({ workType: 'tender', refId: '', activityId: '', hours: Array(7).fill(''), description: '' });
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useDocumentTitle('Timesheet week');

  const start = weekStart(anchor, meta?.settings?.weekStartsOn);
  const days = useMemo(() => Array.from({ length: 7 }, (_, index) => shiftWeek(start, index)), [start]);
  const today = new Date().toISOString().slice(0, 10);
  const rowHours = draft.hours.reduce((sum, value) => sum + (Number(value) || 0), 0);
  const weekHours = rows.reduce((sum, row) => sum + Number(row.hours || 0), 0);

  const load = async (from) => {
    setLoading(true);
    try {
      const response = await platformAPI.listTimesheets({ mine: 'true', from, to: shiftWeek(from, 6) });
      setRows(response.data?.data?.timesheets || []);
    } catch (error) {
      setMessage({ variant: 'danger', text: error.response?.data?.message || 'Unable to load the week' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    platformAPI.timesheetMeta().then((response) => setMeta(response.data?.data || {})).catch(() => setMeta({}));
  }, []);

  useEffect(() => {
    if (start) load(start);
  }, [start]);

  const activities = (meta?.activities || []).filter((item) => item.isActive && (item.workType === 'both' || item.workType === draft.workType));
  const choices = draft.workType === 'assignment' ? (meta?.assignments || []) : (meta?.tenders || []);

  const saveDrafts = async () => {
    const entries = days.flatMap((day, index) => {
      const hours = Number(draft.hours[index]);
      if (!hours) return [];
      return [{
        workType: draft.workType,
        tenderId: draft.workType === 'tender' ? draft.refId : undefined,
        assignmentId: draft.workType === 'assignment' ? draft.refId : undefined,
        activityId: draft.activityId,
        workDate: day,
        hours,
        description: draft.description,
        clientRequestId: `week-${start}-${draft.refId}-${draft.activityId}-${day}`
      }];
    });
    if (!draft.refId || !draft.activityId || !entries.length) {
      setMessage({ variant: 'danger', text: 'Choose the work, an activity, and at least one day with hours.' });
      return;
    }
    setSaving(true);
    try {
      await platformAPI.saveTimesheetWeek({ entries });
      setDraft({ workType: 'tender', refId: '', activityId: '', hours: Array(7).fill(''), description: '' });
      setMessage({ variant: 'success', text: 'Drafts saved. Each day remains its own entry.' });
      await load(start);
    } catch (error) {
      setMessage({ variant: 'danger', text: error.response?.data?.message || 'Could not save the week' });
    } finally {
      setSaving(false);
    }
  };

  const submitSelected = async () => {
    const ids = selected.filter((id) => rows.some((row) => String(row._id) === id && ['draft', 'returned'].includes(row.status)));
    if (!ids.length) return;
    setSaving(true);
    try {
      for (const id of ids) {
        await platformAPI.submitTimesheet(id, {});
      }
      setSelected([]);
      setMessage({ variant: 'success', text: 'Selected entries submitted.' });
      await load(start);
    } catch (error) {
      setMessage({ variant: 'danger', text: error.response?.data?.message || 'Could not submit every selected entry' });
      await load(start);
    } finally {
      setSaving(false);
    }
  };

  const dayTotal = (day) => rows.filter((row) => String(row.workDate).slice(0, 10) === day).reduce((sum, row) => sum + Number(row.hours || 0), 0);

  return (
    <div className="cust-page ts-page">
      <button type="button" className="cust-page__back" onClick={() => navigate('/apps/tender/timesheets')}>
        <BiArrowBack />
        Timesheets
      </button>
      <header className="ts-week__head">
        <div>
          <p className="ts-week__kicker">Weekly entry</p>
          <h1 className="ts-week__range">{formatDay(days[0])} – {formatDay(days[6], true)}</h1>
          <p className="ts-week__lead">Each day is saved as its own entry, so one day can be returned without reopening the rest of the week.</p>
        </div>
        <div className="ts-week__nav">
          <Button size="sm" variant="outline-primary" onClick={() => setAnchor(shiftWeek(start, -7))}>Previous</Button>
          <Button size="sm" variant="outline-primary" onClick={() => setAnchor(today)}>This week</Button>
          <Button size="sm" variant="outline-primary" onClick={() => setAnchor(shiftWeek(start, 7))}>Next</Button>
        </div>
      </header>
      {message ? <Alert variant={message.variant}>{message.text}</Alert> : null}
      <section className="ts-week__composer" aria-label="Add hours for the week">
        <div className="ts-week__choices">
          <label className="ts-week__field">
            <span className="ts-week__label">Work against</span>
            <Form.Select value={draft.workType} onChange={(event) => setDraft({ ...draft, workType: event.target.value, refId: '', activityId: '' })}>
              <option value="tender">Tender</option>
              <option value="assignment">Assignment</option>
            </Form.Select>
          </label>
          <label className="ts-week__field">
            <span className="ts-week__label">{draft.workType === 'assignment' ? 'Assignment' : 'Tender'}</span>
            <Form.Select value={draft.refId} onChange={(event) => setDraft({ ...draft, refId: event.target.value })}>
              <option value="">{draft.workType === 'assignment' ? 'Select an assignment' : 'Select a tender'}</option>
              {choices.map((item) => (
                <option key={item._id} value={item._id}>
                  {draft.workType === 'assignment' ? `${item.reference || item.tenderReference || 'Assignment'} — ${item.name || item.tenderTitle || ''}` : `${item.reference} — ${item.title}`}
                </option>
              ))}
            </Form.Select>
          </label>
          <label className="ts-week__field">
            <span className="ts-week__label">Activity</span>
            <Form.Select value={draft.activityId} onChange={(event) => setDraft({ ...draft, activityId: event.target.value })}>
              <option value="">Select an activity</option>
              {activities.map((item) => <option key={item._id} value={item._id}>{item.name}</option>)}
            </Form.Select>
          </label>
        </div>
        <label className="ts-week__field ts-week__note">
          <span className="ts-week__label">Work description</span>
          <Form.Control value={draft.description} onChange={(event) => setDraft({ ...draft, description: event.target.value })} placeholder="What you worked on. Copied onto each day you fill in." />
        </label>
        <div className="ts-week__days">
          {days.map((day, index) => {
            const booked = dayTotal(day);
            return (
              <label key={day} className={`ts-week__day${day === today ? ' is-today' : ''}`}>
                <span className="ts-week__dow">{WEEKDAYS[new Date(`${day}T00:00:00Z`).getUTCDay()]}</span>
                <span className="ts-week__date">{formatDay(day)}</span>
                <Form.Control
                  type="number"
                  min="0"
                  step={meta?.settings?.hourIncrement || 0.5}
                  aria-label={`Hours on ${formatDay(day)}`}
                  value={draft.hours[index]}
                  onChange={(event) => {
                    const hours = [...draft.hours];
                    hours[index] = event.target.value;
                    setDraft({ ...draft, hours });
                  }}
                />
                <span className="ts-week__booked">{booked ? `${booked}h booked` : '—'}</span>
              </label>
            );
          })}
          <div className="ts-week__row-total">
            <span>This row</span>
            <strong>{rowHours}h</strong>
          </div>
        </div>
        <footer className="ts-week__footer">
          <p>{loading ? 'Updating the week…' : `${weekHours}h already booked this week`}</p>
          <Button size="sm" variant="primary" onClick={saveDrafts} disabled={saving}>{saving ? 'Saving…' : 'Save drafts'}</Button>
        </footer>
      </section>
      {loading && !rows.length ? <Spinner animation="border" size="sm" className="mt-3" /> : (
        <>
          <section className="ts-week__entries">
            <div className="ts-week__entries-head">
              <h2>Entries this week</h2>
              <Button size="sm" variant="outline-primary" onClick={submitSelected} disabled={saving || !selected.length}>Submit selected</Button>
            </div>
            {!rows.length ? <p className="text-muted mb-0">No entries in this week yet.</p> : (
              <Table responsive size="sm">
                <thead><tr><th /><th>Date</th><th>Work</th><th>Activity</th><th>Hours</th><th>Status</th></tr></thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row._id}>
                      <td>
                        {['draft', 'returned'].includes(row.status) ? (
                          <Form.Check checked={selected.includes(String(row._id))} onChange={() => {
                            const id = String(row._id);
                            setSelected((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
                          }} />
                        ) : null}
                      </td>
                      <td>{String(row.workDate).slice(0, 10)}</td>
                      <td>{row.workType === 'assignment' ? (row.assignmentName || 'Assignment') : (row.tenderReference || 'Tender')}</td>
                      <td>{row.activityName}</td>
                      <td>{row.hours}</td>
                      <td><span className={`ts-page__badge ts-page__badge--${row.status}`}>{row.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default TimesheetWeekPage;
